/**
 * The two answers the topic dialog collects, and the rules for reading them back.
 *
 * Kept free of React so the rules can be unit-tested. The topic is deliberately not
 * length-limited here: docs/05-DATA-MODEL.md §7 truncates it when it builds the
 * lecture filename, and that is the only place the length actually matters.
 */

import type { GradeBand } from '@opal/shared';

/** What a lesson is called when the teacher skips the topic — docs/02-SRS.md I3. */
export const UNTITLED_TOPIC = 'Untitled Topic';

/** docs/05-DATA-MODEL.md §2 — a board with no chosen band is a middle-school board. */
export const DEFAULT_GRADE_BAND: GradeBand = 'middle';

/** The three choices in the topic dialog, in the order docs/06-UI-SPEC.md §5 shows them. */
export const GRADE_BAND_CHOICES: readonly { readonly value: GradeBand; readonly label: string }[] =
  [
    { value: 'elementary', label: 'Elementary' },
    { value: 'middle', label: 'Middle' },
    { value: 'high', label: 'High' },
  ];

/** Trims the typed topic and collapses runs of spaces. Blank input becomes an empty string. */
export function normaliseTopic(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/** The topic to show for a lesson, falling back to "Untitled Topic". */
export function topicLabel(topic: string | null): string {
  const normalised = normaliseTopic(topic ?? '');
  return normalised === '' ? UNTITLED_TOPIC : normalised;
}

/** Reads a grade band out of an address. Anything unrecognised becomes the default. */
export function readGradeBand(value: string | null): GradeBand {
  return GRADE_BAND_CHOICES.find((choice) => choice.value === value)?.value ?? DEFAULT_GRADE_BAND;
}

/** The band's dialog label, e.g. 'middle' → "Middle". */
export function gradeBandLabel(band: GradeBand): string {
  return GRADE_BAND_CHOICES.find((choice) => choice.value === band)?.label ?? band;
}
